/**
 * Nowhere Charters — React client entry point.
 *
 * CRA + react-snap setup. The root render uses ReactDOM.hydrateRoot when
 * the page was prerendered (most public routes), and ReactDOM.createRoot
 * otherwise.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';

import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import reportWebVitals from './reportWebVitals';
import './index.css';

// ---- Sentry (browser) ----------------------------------------------------
const dsn = process.env.REACT_APP_SENTRY_DSN;
if (dsn) {
  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV,
    tracesSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0, // record session replay on errors only
    replaysSessionSampleRate: 0.0, // no session replay otherwise
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  });
}

// ---- Root render ---------------------------------------------------------
const rootElement = document.getElementById('root');

const tree = (
  <React.StrictMode>
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  </React.StrictMode>
);

// Some routes are not prerendered, but the SPA fallback serves them the
// public (Landing) prerender shell. Hydrating that would leak the public
// "under construction" content onto them — so /captain* (auth-gated) and
// /sign* (the tokenized guest waiver page) render fresh into a cleared
// container instead of hydrating.
const { pathname } = window.location;
const isFreshRenderRoute =
  pathname.startsWith('/captain') || pathname.startsWith('/sign');

if (rootElement.hasChildNodes() && !isFreshRenderRoute) {
  // react-snap prerendered this public route; hydrate.
  ReactDOM.hydrateRoot(rootElement, tree);
} else {
  // No prerender, or a fresh-render route served the public shell — clear any
  // prerendered markup and render from scratch.
  rootElement.innerHTML = '';
  ReactDOM.createRoot(rootElement).render(tree);
}

reportWebVitals();
